'use client';

import { PropsWithChildren, useEffect, useState, type FC } from 'react';
import { XIcon, PlusIcon, FileText } from 'lucide-react';
import {
  AttachmentPrimitive,
  ComposerPrimitive,
  MessagePrimitive,
  useAuiState,
  useAui,
  type PendingAttachment,
  type CompleteAttachment,
} from '@assistant-ui/react';
import { useShallow } from 'zustand/shallow';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/client/components/ui/tooltip';
import { Dialog, DialogTitle, DialogContent, DialogTrigger } from '@/client/components/ui/dialog';
import { Avatar, AvatarImage, AvatarFallback } from '@/client/components/ui/avatar';
import { TooltipIconButton } from '@/client/components/assistant-ui/tooltip-icon-button';
import { cn } from '@/client/lib/utils';

const useFileSrc = (file: File | undefined) => {
  const [src, setSrc] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!file) {
      setSrc(undefined);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setSrc(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  return src;
};

const useAttachmentSrc = () => {
  const { file, src } = useAuiState(
    useShallow((s): { file?: File; src?: string } => {
      if (s.attachment.type !== 'image') return {};
      if (s.attachment.file) return { file: s.attachment.file };
      const src = s.attachment.content?.filter((c) => c.type === 'image')[0]?.image;
      if (!src) return {};
      return { src };
    }),
  );

  return useFileSrc(file) ?? src;
};

type AttachmentPreviewProps = {
  src: string;
};

const AttachmentPreview: FC<AttachmentPreviewProps> = ({ src }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  return (
    <img
      src={src}
      alt="Image Preview"
      className={cn(
        'block h-auto max-h-[80vh] w-auto max-w-full object-contain',
        isLoaded
          ? 'aui-attachment-preview-image-loaded'
          : 'aui-attachment-preview-image-loading invisible',
      )}
      onLoad={() => setIsLoaded(true)}
    />
  );
};

const AttachmentPreviewDialog: FC<PropsWithChildren> = ({ children }) => {
  const src = useAttachmentSrc();

  if (!src) return children;

  return (
    <Dialog>
      <DialogTrigger
        className="aui-attachment-preview-trigger cursor-pointer transition-colors hover:bg-accent/50"
        asChild
      >
        {children}
      </DialogTrigger>
      <DialogContent className="aui-attachment-preview-dialog-content p-2 sm:max-w-3xl [&>button]:rounded-full [&>button]:bg-foreground/60 [&>button]:p-1 [&>button]:opacity-100 [&>button]:ring-0! [&_svg]:text-background [&>button]:hover:[&_svg]:text-destructive">
        <DialogTitle className="aui-sr-only sr-only">Image Attachment Preview</DialogTitle>
        <div className="aui-attachment-preview relative mx-auto flex max-h-[80dvh] w-full items-center justify-center overflow-hidden bg-background">
          <AttachmentPreview src={src} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

const AttachmentThumb: FC = () => {
  const isImage = useAuiState((s) => s.attachment.type === 'image');
  const src = useAttachmentSrc();

  return (
    <Avatar className="aui-attachment-tile-avatar h-full w-full rounded-none">
      <AvatarImage
        src={src}
        alt="Attachment preview"
        className="aui-attachment-tile-image object-cover"
      />
      <AvatarFallback delayMs={isImage ? 200 : 0}>
        <FileText className="aui-attachment-tile-fallback-icon size-8 text-muted-foreground" />
      </AvatarFallback>
    </Avatar>
  );
};

const AttachmentUI: FC = () => {
  const aui = useAui();
  const isComposer = aui.attachment.source === 'composer';

  const isImage = useAuiState((s) => s.attachment.type === 'image');
  const typeLabel = useAuiState((s) => {
    switch (s.attachment.type) {
      case 'image':
        return 'Image';
      case 'document':
        return 'Document';
      case 'file':
        return 'File';
      default:
        return 'Attachment';
    }
  });

  return (
    <Tooltip>
      <AttachmentPrimitive.Root
        className={cn(
          'aui-attachment-root relative',
          isImage && 'aui-attachment-root-composer only:[&>#attachment-tile]:size-24',
        )}
      >
        <AttachmentPreviewDialog>
          <TooltipTrigger asChild>
            <div
              className={cn(
                'aui-attachment-tile size-14 cursor-pointer overflow-hidden rounded-[14px] border bg-muted transition-opacity hover:opacity-75',
                isComposer && 'aui-attachment-tile-composer border-foreground/20',
              )}
              role="button"
              id="attachment-tile"
              aria-label={`${typeLabel} attachment`}
            >
              <AttachmentThumb />
            </div>
          </TooltipTrigger>
        </AttachmentPreviewDialog>
        {isComposer && <AttachmentRemove />}
      </AttachmentPrimitive.Root>
      <TooltipContent side="top">
        <AttachmentPrimitive.Name />
      </TooltipContent>
    </Tooltip>
  );
};

const AttachmentRemove: FC = () => {
  return (
    <AttachmentPrimitive.Remove asChild>
      <TooltipIconButton
        tooltip="Remove file"
        className="aui-attachment-tile-remove absolute top-1.5 right-1.5 size-3.5 rounded-full bg-white text-muted-foreground opacity-100 shadow-sm hover:bg-white! [&_svg]:text-black hover:[&_svg]:text-destructive"
        side="top"
      >
        <XIcon className="aui-attachment-remove-icon size-3 dark:stroke-[2.5px]" />
      </TooltipIconButton>
    </AttachmentPrimitive.Remove>
  );
};

export const UserMessageAttachments: FC = () => {
  return (
    <div className="aui-user-message-attachments-end col-span-full col-start-1 row-start-1 flex w-full flex-row justify-end gap-2">
      <MessagePrimitive.Attachments components={{ Attachment: AttachmentUI }} />
    </div>
  );
};

export const ComposerAttachments: FC = () => {
  return (
    <div className="aui-composer-attachments mb-2 flex w-full flex-row items-center gap-2 overflow-x-auto px-1.5 pt-0.5 pb-1 empty:hidden">
      <ComposerPrimitive.Attachments components={{ Attachment: AttachmentUI }} />
    </div>
  );
};

export const ComposerAddAttachment: FC = () => {
  return (
    <ComposerPrimitive.AddAttachment asChild>
      <TooltipIconButton
        tooltip="Add Attachment"
        side="top"
        variant="ghost"
        size="icon"
        className="aui-composer-add-attachment size-8.5 rounded-full p-1 font-semibold text-xs hover:bg-muted-foreground/15 dark:border-muted-foreground/15 dark:hover:bg-muted-foreground/30"
        aria-label="Add Attachment"
      >
        <PlusIcon className="aui-attachment-add-icon size-5 stroke-[1.5px]" />
      </TooltipIconButton>
    </ComposerPrimitive.AddAttachment>
  );
};

// ---------------------------------------------------------------------------
// PDF attachment adapter
// ---------------------------------------------------------------------------
// Serializes PDFs as base64 data URLs in a FileMessagePart. The LangGraph
// runtime forwards these as LangChain `file` content blocks, which Anthropic
// Claude natively ingests as document inputs.

const readFileAsDataURL = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });

export class SimplePDFAttachmentAdapter {
  public accept = 'application/pdf';

  public async add(state: { file: File }): Promise<PendingAttachment> {
    return {
      id: state.file.name,
      type: 'document',
      name: state.file.name,
      contentType: state.file.type || 'application/pdf',
      file: state.file,
      status: { type: 'requires-action', reason: 'composer-send' },
    };
  }

  public async send(attachment: PendingAttachment): Promise<CompleteAttachment> {
    const dataUrl = await readFileAsDataURL(attachment.file);
    // Anthropic's `document.source.base64` field expects raw base64, not a
    // data URL — strip the `data:<mime>;base64,` prefix that FileReader emits.
    const base64 = dataUrl.replace(/^data:[^;]+;base64,/, '');
    return {
      ...attachment,
      status: { type: 'complete' },
      content: [
        {
          type: 'file',
          filename: attachment.name,
          data: base64,
          mimeType: attachment.contentType || 'application/pdf',
        },
      ],
    };
  }

  public async remove(): Promise<void> {
    // noop
  }
}

// ---------------------------------------------------------------------------
// User message media parts (image / file)
// ---------------------------------------------------------------------------
// Images and file attachments sent by the user round-trip through the backend
// as LangChain content blocks (image_url / file). On replay they land in
// `message.parts` as `image` / `file` parts — assistant-ui's default
// UserMessage renderer drops them. These components render the parts inline
// above the text bubble.

type ImagePart = { type: 'image'; image: string; filename?: string };
type FilePart = {
  type: 'file';
  filename?: string;
  data: string;
  mimeType: string;
};
type MediaPart = ImagePart | FilePart;

const UserImagePreview: FC<{ src: string }> = ({ src }) => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="aui-user-message-image-trigger overflow-hidden rounded-2xl border bg-muted transition-opacity hover:opacity-90"
          aria-label="Open image preview"
        >
          <img
            src={src}
            alt="Attachment"
            className="aui-user-message-image block max-h-64 max-w-full object-contain"
          />
        </button>
      </DialogTrigger>
      <DialogContent className="aui-user-message-image-dialog p-2 sm:max-w-3xl [&>button]:rounded-full [&>button]:bg-foreground/60 [&>button]:p-1 [&>button]:opacity-100 [&>button]:ring-0! [&_svg]:text-background [&>button]:hover:[&_svg]:text-destructive">
        <DialogTitle className="sr-only">Image Attachment Preview</DialogTitle>
        <div className="relative mx-auto flex max-h-[80dvh] w-full items-center justify-center overflow-hidden bg-background">
          <img
            src={src}
            alt="Attachment preview"
            className="block h-auto max-h-[80vh] w-auto max-w-full object-contain"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

const UserFilePreview: FC<{
  filename?: string;
  mimeType: string;
  data: string;
}> = ({ filename, mimeType, data }) => {
  const displayName = filename ?? 'Document';
  const subtype = mimeType.split('/')[1] ?? 'file';
  const label = mimeType === 'application/pdf' ? 'PDF' : subtype.toUpperCase();
  const href = data.startsWith('data:') ? data : `data:${mimeType};base64,${data}`;
  return (
    <a
      href={href}
      download={filename}
      target="_blank"
      rel="noreferrer"
      className="aui-user-message-file inline-flex max-w-full items-center gap-2 rounded-2xl border bg-muted px-3 py-2 text-sm transition-colors hover:bg-accent"
    >
      <FileText className="aui-user-message-file-icon size-5 shrink-0 text-muted-foreground" />
      <span className="flex min-w-0 flex-col text-left">
        <span className="max-w-[16rem] truncate font-medium">{displayName}</span>
        <span className="text-xs text-muted-foreground">{label}</span>
      </span>
    </a>
  );
};

export const UserMessageMediaParts: FC = () => {
  const parts = useAuiState(
    useShallow((s) => {
      const msgParts = s.message.parts ?? [];
      return msgParts.filter(
        (p) => p.type === 'image' || p.type === 'file',
      ) as unknown as MediaPart[];
    }),
  );

  if (parts.length === 0) return null;

  return (
    <div className="aui-user-message-media col-span-full col-start-1 row-start-2 flex flex-row flex-wrap justify-end gap-2">
      {parts.map((part, index) =>
        part.type === 'image' ? (
          <UserImagePreview key={index} src={part.image} />
        ) : (
          <UserFilePreview
            key={index}
            filename={part.filename}
            mimeType={part.mimeType}
            data={part.data}
          />
        ),
      )}
    </div>
  );
};
